import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import API_CONFIG from '../config/api';

const VerifyOtp: React.FC = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email') || '';

  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (!/^[0-9]*$/.test(element.value)) return;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    // Focus next input
    if (element.value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    // Extract only digits from pasted text
    const data = pastedData.replace(/\D/g, '').split('').slice(0, 6);
    
    if (data.length > 0) {
      const newOtp = [...otp];
      data.forEach((char, idx) => {
        if (idx < 6) newOtp[idx] = char;
      });
      setOtp(newOtp);
      // Focus the last input or the one after the pasted digits
      const nextIndex = Math.min(data.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/verify-email-otp`, {
        email,
        otp: otpValue
      });

      if (response.data.success) {
        toast.success('Email verified successfully!');
        navigate('/login');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setResendLoading(true);
    try {
      await axios.post(`${API_CONFIG.BASE_URL}/auth/resend-email-otp`, { email });
      toast.success('New OTP sent to your email');
      setCountdown(60);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/signup" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6 font-medium">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Signup
          </Link>
          <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-secondary-900">Verify your email</h1>
          <p className="mt-2 text-secondary-600">
            We've sent a 6-digit code to <span className="font-semibold">{email}</span>
          </p>
        </div>

        <div className="card shadow-xl border-none">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="flex justify-between gap-2">
              {otp.map((data, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={index === 0 ? "one-time-code" : undefined}
                  ref={el => { inputRefs.current[index] = el; }}
                  value={data}
                  onChange={e => handleChange(e.target, index)}
                  onKeyDown={e => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all bg-secondary-50"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={isLoading || otp.join('').length !== 6}
              className="w-full btn-primary py-4 text-lg text-white font-bold rounded-xl shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all active:scale-95"
            >
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>

            <div className="text-center space-y-4">
              <p className="text-secondary-600">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResend}
                disabled={countdown > 0 || resendLoading}
                className="inline-flex items-center text-primary-600 hover:text-primary-700 font-bold disabled:text-secondary-400 group"
              >
                {resendLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className={`h-4 w-4 mr-2 transition-transform ${countdown === 0 ? 'group-hover:rotate-180' : ''}`} />
                )}
                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-secondary-500">
          Make sure to check your spam folder if you don't see the email.
        </p>
      </div>
    </div>
  );
};

export default VerifyOtp;
